import { useEffect, useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Settings } from "lucide-react";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { createCheckoutSession } from "@/utils/payments.functions";
import { Button } from "@/components/ui/button";
import { useManageSubscription } from "@/hooks/useManageSubscription";

interface Props {
  priceId: string;
  returnUrl?: string;
}

type State =
  | { status: "loading" }
  | { status: "ready"; clientSecret: string }
  | { status: "already_subscribed"; message: string; portalUrl?: string }
  | { status: "error"; message: string };

export function StripeEmbeddedCheckout({ priceId, returnUrl }: Props) {
  const [state, setState] = useState<State>({ status: "loading" });
  const { manage, loading: portalLoading } = useManageSubscription();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await createCheckoutSession({
          data: {
            priceId,
            returnUrl: returnUrl || window.location.href,
            environment: getStripeEnvironment(),
          },
        });
        if (cancelled) return;
        if ("clientSecret" in result) {
          if (!result.clientSecret) {
            setState({ status: "error", message: "Stripe did not return a client secret." });
            return;
          }
          setState({ status: "ready", clientSecret: result.clientSecret });
          return;
        }
        if (result.code === "already_subscribed") {
          setState({
            status: "already_subscribed",
            message: result.error,
            portalUrl: result.portalUrl,
          });
          return;
        }
        setState({ status: "error", message: result.error });
      } catch (err) {
        if (cancelled) return;
        setState({ status: "error", message: (err as Error).message || "Checkout failed." });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [priceId, returnUrl]);

  if (state.status === "loading") {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">Preparing checkout…</div>
    );
  }

  if (state.status === "already_subscribed") {
    return (
      <div className="p-8 text-center">
        <h2 className="font-display text-xl font-semibold">You're already subscribed</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{state.message}</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {state.portalUrl ? (
            <Button
              size="lg"
              className="shadow-glow"
              onClick={() => window.open(state.portalUrl!, "_blank")}
            >
              <Settings className="mr-2 h-4 w-4" />
              Open billing portal
            </Button>
          ) : (
            <Button size="lg" className="shadow-glow" onClick={manage} disabled={portalLoading}>
              <Settings className="mr-2 h-4 w-4" />
              {portalLoading ? "Opening…" : "Manage subscription"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="p-8 text-center text-sm text-destructive">{state.message}</div>
    );
  }

  return (
    <div id="checkout">
      <EmbeddedCheckoutProvider
        stripe={getStripe()}
        options={{ clientSecret: state.clientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
